'use client'
import { useCallback, useEffect, useState } from 'react'
import { useIsAuthenticated } from '@domain/auth/Auth.context'
import { useMe } from '@domain/me'
import { CURRENT_TERMS_VERSION_ID } from './termsUpdateAck.constants'
import { needsTermsUpdateAckForVersion } from './termsAck.storage'
import { useAcknowledgeTermsUpdate } from './api/useAcknowledgeTermsUpdate.api'

/**
 * Shows the Terms update Got it card when the signed-in user's profile
 * `meta.termsAckVersionId` does not match CURRENT_TERMS_VERSION_ID.
 */
export const useTermsUpdateAck = () => {
  const isAuthenticated = useIsAuthenticated()
  const { data: me, isLoading: isMeLoading } = useMe()
  const { mutateAsync: saveTermsAck, isLoading: isSavingMutation } =
    useAcknowledgeTermsUpdate()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      setOpen(false)
      return
    }
    if (isMeLoading || !me?.uuid) {
      return
    }
    setOpen(needsTermsUpdateAckForVersion(me.meta?.termsAckVersionId))
  }, [isAuthenticated, isMeLoading, me?.uuid, me?.meta?.termsAckVersionId])

  const handleGotIt = useCallback(async () => {
    setError(null)
    setIsSaving(true)
    try {
      await saveTermsAck(CURRENT_TERMS_VERSION_ID)
      setOpen(false)
    } catch {
      setError("Couldn't save acknowledgement. Try again.")
    } finally {
      setIsSaving(false)
    }
  }, [saveTermsAck])

  return {
    open: isAuthenticated && open,
    error,
    isSaving: isSaving || isSavingMutation,
    handleGotIt,
    currentVersionId: CURRENT_TERMS_VERSION_ID,
  }
}
